import React, { useEffect } from 'react'
import { useSetState } from 'react-use'
import PropTypes from 'prop-types'
import { flowRight, get } from 'lodash'
import { graphql } from '@apollo/client/react/hoc'
import { withErrorBoundaryNoRender } from 'lib/errorBoundary'
import { useTranslation } from 'react-i18next'
import { withRouter } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { fetchPolicy } from 'lib/apolloFetchPolicy'
import { canShareBos, getBosSharePath } from 'lib/utils/store/bos'
import { logException } from 'lib/errors'

import AppContext from 'components/AppContext'
import BosShareLogic from 'components/BillOfSale/BosShareLogic'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import Button from 'components/UI/Button'
import FormElement from 'components/Forms/FormElements/FormElement'
import DoneIndicator from 'components/UI/DoneIndicator/DoneIndicator'
import LoadingIndicator from 'components/LoadingIndicator'
import ErrorComponent from 'components/Errors/ErrorComponent'
import Loading from 'components/Util/Loading'
import StatusMessage from 'components/StatusMessage'
import BosShareUrlInput from './BosShareUrlInput/BosShareUrlInput'
import BosLink from 'lib/utils/store/wrappers/bos/BosLink'

import IconCheckGreen from 'images/svg/icon/icon-icon-check-circle-green.svg'

import getBosShare from 'graphql/bos/getBosShare.gql'
import sendBosShareLink from 'graphql/bos/sendBosShareLink.gql'
import createBosAccessHash from 'graphql/bos/createBosAccessHash.gql'

import './bos-share.pcss'

const BosShare = ({ data, onCloseCalled }) => {
    const [state, setState] = useSetState({
        copyCount: 0,
        bosLink: null,
        linkDeleted: null,
        mailSentToBuyer: false
    })

    const [sendBosShareLinkInEmail] = useMutation(sendBosShareLink)
    const [createNewBosAccessHash] = useMutation(createBosAccessHash)

    const { t, i18n } = useTranslation('billOfSale')
    const { copyCount, bosLink, linkDeleted, mailSentToBuyer } = state
    const { loading, error, bos } = data

    // first, check if bos already has active bosLink and use that
    const shareBosLink = bosLink || get(bos, 'bosLink', null)
    const wrappedBosLink = shareBosLink ? BosLink.create(shareBosLink) : null
    const buyer = get(bos, 'buyer', null)

    useEffect(() => {
        if (wrappedBosLink && !wrappedBosLink.isActive()) {
            createNewBosAccessHash({
                variables: {
                    bosId: parseInt(bos.id)
                }
            }).then(result => {
                setState({
                    bosLink: result.data.createBosAccessHash,
                })
            })
        }
    }, [wrappedBosLink, bos?.id, createNewBosAccessHash, setState])

    if (error) return <ErrorComponent error={error} />
    if (loading) return <LoadingIndicator />

    if (!canShareBos(bos)) {
        return (
            <StatusMessage type="highlight">
                {t('billOfSale:share.cantShareBosMessage')}
            </StatusMessage>
        )
    }

    const renderBuyerMailSentMessage = () => {
        return (
            <div className="bos-share__preview-details">
                <IconCheckGreen />
                <p>{t('billOfSale:share.previewMessage')}</p>
                <div className="action-wrapper">
                    {onCloseCalled && (
                        <Button
                            className="button button--secondary"
                            trackName="close"
                            onClick={onCloseCalled}
                        >
                            {t('billOfSale:share.actions.closeWindow')}
                        </Button>
                    )}
                </div>
            </div>
        )
    }

    const renderLinkAndActions = (bosShareLogic) => {
        const bosShareUrl = getBosSharePath(bos.id, wrappedBosLink.getLinkHash(), i18n.language, true)
        const expiryText = t('billOfSale:share.linkExpiryLabel', {
            expireDate: wrappedBosLink.getExpireTime('DD.MM.YYYY HH:mm'),
        })

        return (
            <div className="bos-share">
                {!buyer.buyerEmail && (
                    <div className="bos-share__warning-message">
                        <StatusMessage type="alert" icon="icon_alert_circle_yellow">{t('billOfSale:share.errorMessage')}</StatusMessage>
                    </div>
                )}
                <p>{t('billOfSale:share.infoText')}</p>
                <div>
                    <FormElement description={expiryText}>
                        {() => (
                            <BosShareUrlInput
                                key={bosShareUrl}
                                animateIn={!!bosLink}
                                bosShareUrl={bosShareUrl}
                            />
                        )}
                    </FormElement>
                </div>

                <div className="action-wrapper">
                    <Button
                        className="button button--primary"
                        trackName="sendEmailToBuyer"
                        disabled={!buyer.buyerEmail}
                        onClick={() => {
                            const bosId = bos.id
                            sendBosShareLinkInEmail({
                                variables: { bosId }
                            }).then(() => {
                                setState({ mailSentToBuyer: true })
                            }).catch(logException)
                        }}
                    >
                        {t('billOfSale:share.actions.sendEmailToBuyer')}
                    </Button>
                    <CopyToClipboard
                        text={bosShareUrl}
                        onCopy={() => {
                            setState({ copyCount: copyCount + 1 })
                        }}
                    >
                        <Button
                            className="button button--secondary"
                            trackName="copyLink"
                        >
                            {t('billOfSale:share.actions.copyLink')}
                        </Button>
                    </CopyToClipboard>
                    {(copyCount > 0) && (
                        <DoneIndicator
                            key={copyCount}
                            className="action-wrapper-item"
                            text={t('billOfSale:share.copied')}
                        />
                    )}
                    <Button
                        className="button button--tertiary-delete"
                        trackName="deleteLink"
                        onClick={async () => {
                            await bosShareLogic.deleteBosAccessHash(bos.id, wrappedBosLink.getId())
                                .then(() => {
                                    setState({
                                        linkDeleted: true,
                                        copyCount: 0,
                                    })
                                })
                        }}
                    >
                        {t('billOfSale:share.actions.deleteLink')}
                    </Button>
                </div>
            </div>
        )
    }

    const renderLinkDeleted = () => {
        return (
            <div className="bos-share__delete-details">
                <IconCheckGreen />
                <p>{t('billOfSale:share.deletedMessage')}</p>
                <div className="action-wrapper">
                    {onCloseCalled && (
                        <Button
                            className="button button--secondary"
                            trackName="close"
                            onClick={onCloseCalled}
                        >
                            {t('billOfSale:share.actions.closeWindow')}
                        </Button>
                    )}
                </div>
            </div>
        )
    }

    const createNewBosLink = !wrappedBosLink

    return (
        <AppContext context="bosShare">
            <BosShareLogic
                initState={{ loading: createNewBosLink }}
                onMount={async (bosShareLogic) => {
                    if (createNewBosLink) {
                        await bosShareLogic.createBosAccessHash(bos.id)
                            .then(result => {
                                setState({
                                    bosLink: result.data.createBosAccessHash,
                                })
                            })
                    }
                }}
            >
                {(bosShareLogic) => (
                    <Loading loading={bosShareLogic.loading} error={bosShareLogic.error}>
                        {() => {
                            if (linkDeleted) {
                                return renderLinkDeleted()
                            }

                            if (mailSentToBuyer) {
                                return renderBuyerMailSentMessage()
                            }

                            if (wrappedBosLink) {
                                return renderLinkAndActions(bosShareLogic)
                            }

                            return null
                        }}
                    </Loading>
                )}
            </BosShareLogic>
        </AppContext>
    )
}

BosShare.propTypes = {
    bosId: PropTypes.string.isRequired,
    onCloseCalled: PropTypes.func,
}

export default flowRight(
    withErrorBoundaryNoRender,
    withRouter,
    graphql(getBosShare, {
        options: props => ({
            variables: {
                bosId: props.bosId,
            },
            fetchPolicy: fetchPolicy.NETWORK_ONLY,
        })
    }),
)(BosShare)
